import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import {
  Network, Server, Shield, AlertTriangle, RefreshCcw, ZoomIn, ZoomOut,
  Maximize, Download, Eye, EyeOff, Layers, Globe, Building2,
  Monitor, Laptop, Smartphone, Printer, Wifi, HardDrive, Router,
  ChevronRight, X, ExternalLink, Activity, Lock, Unlock, Target,
  Play, Pause, SkipForward, Crosshair, Brain, Zap, Route, ArrowRight,
  Clock, CheckCircle, XCircle, Info, ChevronDown, ChevronUp, Sparkles,
  Send, MessageSquare, Scan, Terminal, FileText, Copy, Settings, Search,
  MoreVertical, Trash2, Edit3, Power, Database, Bug, Radio, Cpu, Briefcase,
  ShieldAlert
} from 'lucide-react';
import * as d3 from 'd3';
import { scans as scansApi, workspaces as workspacesApi } from '../../api/client';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import { usePageTitle } from '../../hooks/usePageTitle';
import useWebSocket from '../../hooks/useWebSocket';
import { getWebSocketUrl } from '../../utils/websocket';

// OS Icon Components
const WindowsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
  </svg>
);

const LinuxIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.19.135.33.332.438.533.105.259.158.459.166.724 0-.02.006-.04.006-.06v.105a.086.086 0 01-.004-.021l-.004-.024a1.807 1.807 0 01-.15.706.953.953 0 01-.213.335.71.71 0 00-.088-.042c-.104-.045-.198-.064-.284-.133a1.312 1.312 0 00-.22-.066c.05-.06.146-.133.183-.198.053-.128.082-.264.088-.402v-.02a1.21 1.21 0 00-.061-.4c-.045-.134-.101-.2-.183-.333-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132a.8.8 0 00-.205.334 1.18 1.18 0 00-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202a1.635 1.635 0 01-.018-.2v-.02a1.772 1.772 0 01.15-.768c.082-.22.232-.406.43-.533a.985.985 0 01.594-.2zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.14.4.153.667v.004c.007.134.006.2-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.09.013-.18.003-.267v-.015c-.012-.133-.04-.2-.082-.333a.613.613 0 00-.166-.267.248.248 0 00-.183-.064h-.021c-.071.006-.13.04-.186.132a.552.552 0 00-.12.27.944.944 0 00-.023.33v.015c.012.135.037.2.08.334.046.134.098.2.166.268.01.009.02.018.034.024-.07.057-.117.07-.176.136a.304.304 0 01-.131.068 2.62 2.62 0 01-.275-.402 1.772 1.772 0 01-.155-.667 1.759 1.759 0 01.08-.668 1.43 1.43 0 01.283-.535c.128-.133.26-.2.418-.2zm1.37 1.706c.332 0 .733.065 1.216.399.293.2.523.269 1.052.468h.003c.255.136.405.266.478.399v-.131a.571.571 0 01.016.47c-.123.31-.516.643-1.063.842v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267a1.139 1.139 0 01-.448-.067 3.566 3.566 0 01-.322-.198c-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.71-.07-.268-.005-.47.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.47.839-.601.139-.036.294-.065.466-.065zm2.8 2.142c.358 1.417 1.196 3.475 1.735 4.473.286.534.855 1.659 1.102 3.024.156-.005.33.018.513.064.646-1.671-.546-3.467-1.089-3.966-.22-.2-.232-.335-.123-.335.59.534 1.365 1.572 1.646 2.757.13.535.16 1.104.021 1.67.067.028.135.06.205.067 1.032.534 1.413.938 1.23 1.537v-.002c-.06-.135-.12-.2-.184-.268h-.004c-.048-.07-.105-.134-.2-.202.205.532.227 1.066.133 1.533-.15.832-.582 1.472-1.006 1.875-.238.27-.4.465-.676.602-.28.135-.598.2-1.016.068-.328-.101-.623-.299-.85-.532-.257-.268-.378-.6-.505-.932-.063-.171-.098-.268-.078-.399.12-.135.267-.132.377-.132.075 0 .137.004.185.01.043-.066.08-.132.126-.2a.555.555 0 00.095-.2c-.06-.028-.133-.064-.195-.132a.557.557 0 01-.143-.267c-.064-.268-.07-.6.083-.867.07-.135.132-.201.225-.266.094-.066.197-.135.33-.2a1.327 1.327 0 00-.3-.082c-.163-.028-.323-.05-.48-.128l-.001-.002a.846.846 0 01-.167-.1.621.621 0 01-.265-.398c-.027-.165-.018-.268.008-.4.096-.406.296-.744.546-.99.17-.165.354-.267.538-.333v-.013c-.017-.151-.053-.266-.12-.4a1.35 1.35 0 00-.258-.367c-.263-.332-.573-.601-.745-.8a.566.566 0 01-.115-.2c-.022-.067-.032-.132-.03-.133-.003.003-.009.136-.009.2 0 .065.003.132.009.2a.759.759 0 00.1.334c-.057.028-.117.064-.167.133a.816.816 0 00-.1.2.721.721 0 00-.05.267c0 .067.01.132.027.2.03.132.066.2.105.267a.893.893 0 00.12.183c-.053.028-.098.064-.14.1-.057.057-.107.1-.147.153a.77.77 0 00-.093.2c-.037.133-.037.267 0 .4.036.134.096.2.157.268.061.066.13.133.21.2a.96.96 0 00.268.132c-.17.066-.333.133-.495.266-.197.165-.33.4-.406.667-.07.268-.07.535-.036.797l.005.027c-.095.066-.155.133-.203.199a.685.685 0 00-.101.266.767.767 0 00-.01.267c.009.066.025.132.046.2h-.001c-.028 0-.06.005-.091.006h-.022a.665.665 0 01-.333-.066 1.12 1.12 0 01-.267-.2 3.632 3.632 0 01-.533-.598c-.265-.4-.47-.867-.536-1.4a3.553 3.553 0 01.134-1.334c.136-.466.376-.862.672-1.199.143-.17.303-.315.48-.44.144-.1.3-.187.464-.258-.3-.9-.436-1.867-.436-2.799 0-1.066.201-2.066.568-2.932-.174-.066-.32-.132-.467-.199-.19-.068-.396-.135-.596-.2l-.005.002h.002z"/>
  </svg>
);

const AppleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
  </svg>
);

const AndroidIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.523 15.34a.558.558 0 00-.554.56v4.1a.558.558 0 001.112 0v-4.1a.558.558 0 00-.558-.56zm-11.046 0a.558.558 0 00-.554.56v4.1a.558.558 0 001.112 0v-4.1a.558.558 0 00-.558-.56zm8.626-10.24l.941-1.653a.187.187 0 00-.074-.25.188.188 0 00-.253.07l-.955 1.677c-.753-.338-1.597-.53-2.491-.53-.894 0-1.738.192-2.491.53L8.826 3.267a.188.188 0 00-.327.18l.941 1.653c-1.694.907-2.84 2.548-2.84 4.413h10.8c0-1.865-1.146-3.506-2.84-4.413zm-4.87 2.393a.558.558 0 110-1.116.558.558 0 010 1.116zm4.334 0a.558.558 0 110-1.116.558.558 0 010 1.116zM5.6 10.58h12.8v8.8c0 .75-.61 1.36-1.36 1.36h-.8v2.46c0 .43-.35.8-.8.8s-.8-.37-.8-.8v-2.46H9.36v2.46c0 .43-.35.8-.8.8s-.8-.37-.8-.8v-2.46h-.8c-.75 0-1.36-.61-1.36-1.36v-8.8z"/>
  </svg>
);

const ProxmoxIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const SwitchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2"/>
    <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
    <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
    <circle cx="14" cy="12" r="1.5" fill="currentColor"/>
    <circle cx="18" cy="12" r="1.5" fill="currentColor"/>
  </svg>
);

const FirewallIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2s8 4 8 10v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5C4 6 12 2 12 2z"/>
    <path d="M8 12h8M8 16h8"/>
  </svg>
);

const AccessPointIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
    <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <circle cx="12" cy="20" r="1" fill="currentColor"/>
  </svg>
);

const NetworkIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="16" y="16" width="6" height="6" rx="1"/>
    <rect x="2" y="16" width="6" height="6" rx="1"/>
    <rect x="9" y="2" width="6" height="6" rx="1"/>
    <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"/>
    <path d="M12 12V8"/>
  </svg>
);

// Helper function to get OS icon based on os_name
const getOSIcon = (osName) => {
  if (!osName) return { icon: Terminal, color: 'text-gray-400', name: 'Inconnu' };

  const osLower = osName.toLowerCase();

  // Windows detection
  if (osLower.includes('windows') || osLower.includes('win32') || osLower.includes('win64') || osLower.includes('microsoft')) {
    return { icon: WindowsIcon, color: 'text-blue-400', name: 'Windows' };
  }

  // macOS / Apple detection
  if (osLower.includes('mac') || osLower.includes('darwin') || osLower.includes('apple') || osLower.includes('ios') || osLower.includes('osx')) {
    return { icon: AppleIcon, color: 'text-gray-300', name: 'macOS' };
  }

  // Android detection
  if (osLower.includes('android')) {
    return { icon: AndroidIcon, color: 'text-green-400', name: 'Android' };
  }

  // Linux distributions
  if (osLower.includes('linux') || osLower.includes('ubuntu') || osLower.includes('debian') ||
      osLower.includes('centos') || osLower.includes('redhat') || osLower.includes('fedora') ||
      osLower.includes('kali') || osLower.includes('arch') || osLower.includes('mint') ||
      osLower.includes('suse') || osLower.includes('gentoo') || osLower.includes('manjaro')) {
    return { icon: LinuxIcon, color: 'text-yellow-400', name: 'Linux' };
  }

  // BSD variants
  if (osLower.includes('freebsd') || osLower.includes('openbsd') || osLower.includes('netbsd') || osLower.includes('bsd')) {
    return { icon: Terminal, color: 'text-red-400', name: 'BSD' };
  }

  // Unix
  if (osLower.includes('unix') || osLower.includes('solaris') || osLower.includes('aix') || osLower.includes('hp-ux')) {
    return { icon: Terminal, color: 'text-purple-400', name: 'Unix' };
  }

  // Proxmox - specific detection before general hypervisor
  if (osLower.includes('proxmox') || osLower.includes('pve')) {
    return { icon: ProxmoxIcon, color: 'text-orange-500', name: 'Proxmox' };
  }

  // VMware / Hypervisor
  if (osLower.includes('vmware') || osLower.includes('esxi') || osLower.includes('hypervisor') ||
      osLower.includes('hyper-v') || osLower.includes('xen') || osLower.includes('kvm')) {
    return { icon: Server, color: 'text-teal-400', name: 'Hyperviseur' };
  }

  // Firewalls
  if (osLower.includes('fortinet') || osLower.includes('fortigate') || osLower.includes('palo alto') ||
      osLower.includes('pfsense') || osLower.includes('opnsense') || osLower.includes('sophos') ||
      osLower.includes('watchguard') || osLower.includes('checkpoint') || osLower.includes('firewall')) {
    return { icon: FirewallIcon, color: 'text-red-500', name: 'Pare-feu' };
  }

  // Routers
  if (osLower.includes('cisco') || osLower.includes('juniper') || osLower.includes('mikrotik') ||
      osLower.includes('router') || osLower.includes('vyos') || osLower.includes('openwrt') ||
      osLower.includes('routeros')) {
    return { icon: Router, color: 'text-cyan-400', name: 'Routeur' };
  }

  // Switches
  if (osLower.includes('switch') || osLower.includes('aruba') || osLower.includes('procurve') ||
      osLower.includes('catalyst') || osLower.includes('nexus')) {
    return { icon: SwitchIcon, color: 'text-blue-500', name: 'Commutateur' };
  }

  // Network equipment brands (HP, Netgear, Ubiquiti, etc.)
  if (osLower.includes('hp ') || osLower.includes('hewlett') || osLower.includes('hpe ') ||
      osLower.includes('netgear') || osLower.includes('tp-link') || osLower.includes('tplink') ||
      osLower.includes('d-link') || osLower.includes('dlink') || osLower.includes('linksys') ||
      osLower.includes('asus') || osLower.includes('zyxel')) {
    return { icon: NetworkIcon, color: 'text-cyan-400', name: 'Équipement réseau' };
  }

  // Ubiquiti / UniFi
  if (osLower.includes('ubiquiti') || osLower.includes('unifi') || osLower.includes('edgerouter') ||
      osLower.includes('edgeswitch') || osLower.includes('ubnt')) {
    return { icon: AccessPointIcon, color: 'text-blue-400', name: 'Ubiquiti' };
  }

  // Access Points / WiFi
  if (osLower.includes('access point') || osLower.includes('ap ') || osLower.includes('wifi') ||
      osLower.includes('wireless') || osLower.includes('ruckus') || osLower.includes('meraki')) {
    return { icon: AccessPointIcon, color: 'text-green-400', name: 'Point d\'accès' };
  }

  // IoT / Embedded
  if (osLower.includes('embedded') || osLower.includes('rtos') || osLower.includes('firmware') ||
      osLower.includes('iot') || osLower.includes('raspberry') || osLower.includes('arduino') ||
      osLower.includes('esp32') || osLower.includes('esp8266')) {
    return { icon: HardDrive, color: 'text-orange-400', name: 'Embarqué' };
  }

  // Printer
  if (osLower.includes('printer') || osLower.includes('hp laserjet') || osLower.includes('canon') ||
      osLower.includes('xerox') || osLower.includes('epson') || osLower.includes('brother')) {
    return { icon: Printer, color: 'text-pink-400', name: 'Imprimante' };
  }

  // Phone
  if (osLower.includes('phone') || osLower.includes('mobile')) {
    return { icon: Smartphone, color: 'text-indigo-400', name: 'Mobile' };
  }

  // Default
  return { icon: Terminal, color: 'text-gray-400', name: 'Inconnu' };
};

const NetworkMap = () => {
  const { t } = useI18n();
  usePageTitle('Carte Réseau - Visualisation Topologie');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  // Workspace state
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(searchParams.get('workspace') || null);
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);

  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedHost, setSelectedHost] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [showVulnOnly, setShowVulnOnly] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [expandedServices, setExpandedServices] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Node Action Menu
  const [nodeMenuOpen, setNodeMenuOpen] = useState(null);
  const [nodeMenuPosition, setNodeMenuPosition] = useState({ x: 0, y: 0 });

  // AI Assistant States
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Quick Actions Modal
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [quickActionTarget, setQuickActionTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  // Attack Simulator
  const [attackSimulatorEnabled, setAttackSimulatorEnabled] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [attackPath, setAttackPath] = useState([]);
  const [attackStep, setAttackStep] = useState(0);
  const [isAttackRunning, setIsAttackRunning] = useState(false);
  const [attackResults, setAttackResults] = useState([]);

  // WebSocket for dynamic updates
  const { lastMessage } = useWebSocket(getWebSocketUrl('/ws'));

  // Fetch workspaces on mount
  useEffect(() => {
    fetchWorkspaces();
  }, []);

  // Fetch scans when workspace changes
  useEffect(() => {
    fetchScans();
  }, [selectedWorkspace]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === 'host_discovered') {
      const newHost = lastMessage.host;
      setHosts(prevHosts => {
        if (prevHosts.find(h => h.ip === newHost.ip)) return prevHosts;
        return [...prevHosts, newHost];
      });
    }
  }, [lastMessage]);

  useEffect(() => {
    if (selectedScan) {
      fetchHostsForScan(selectedScan);
    }
  }, [selectedScan]);

  useEffect(() => {
    if (hosts.length > 0 && svgRef.current) {
      renderNetworkGraph();
    }
  }, [hosts, showLabels, showVulnOnly, animationsEnabled, selectedTarget, attackPath, attackStep, selectedHost]);

  const fetchWorkspaces = async () => {
    try {
      const data = await workspacesApi.list(0, 100, null, true);
      setWorkspaces(data.workspaces || []);
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    }
  };

  const fetchScans = async () => {
    // Reset immédiatement au changement de workspace
    setScans([]);
    setSelectedScan(null);
    setHosts([]);
    setLoading(true);
    try {
      let completedScans = [];
      if (selectedWorkspace) {
        // Fetch scans for the selected workspace
        const data = await workspacesApi.getScans(selectedWorkspace, 0, 100);
        completedScans = (data.scans || []).filter(s => s.status === 'completed');
      } else {
        // Fetch all scans
        const data = await scansApi.list(0, 100);
        completedScans = (data.scans || []).filter(s => s.status === 'completed');
      }
      setScans(completedScans);
      if (completedScans.length > 0) {
        setSelectedScan(completedScans[0].id);
      } else {
        setSelectedScan(null);
        setHosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceChange = (workspaceId) => {
    setSelectedWorkspace(workspaceId);
    setWorkspaceDropdownOpen(false);
    // Update URL params
    if (workspaceId) {
      setSearchParams({ workspace: workspaceId });
    } else {
      setSearchParams({});
    }
  };

  const fetchHostsForScan = async (scanId) => {
    setLoading(true);
    try {
      const hostsData = await scansApi.getHosts(scanId);
      setHosts(hostsData || []);
    } catch (error) {
      console.error('Failed to fetch hosts:', error);
      setHosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Get device type icon based on OS/hostname
  const getDeviceType = (host) => {
    const hostname = (host.hostname || '').toLowerCase();
    const os = (host.os_name || '').toLowerCase();

    if (hostname.includes('router') || hostname.includes('gateway') || hostname.includes('livebox')) return 'router';
    if (hostname.includes('nas') || hostname.includes('storage') || hostname.includes('qnap') || hostname.includes('synology')) return 'storage';
    if (hostname.includes('printer') || hostname.includes('print')) return 'printer';
    if (hostname.includes('phone') || hostname.includes('mobile') || hostname.includes('iphone') || hostname.includes('android')) return 'smartphone';
    if (hostname.includes('laptop') || hostname.includes('notebook') || hostname.includes('macbook')) return 'laptop';
    if (hostname.includes('wifi') || hostname.includes('ap')) return 'wifi';
    if (os.includes('windows server') || os.includes('linux') && hostname.includes('srv')) return 'server';
    if (os.includes('windows')) return 'workstation';
    if (os.includes('linux')) return 'server';
    return 'workstation';
  };

  const DeviceIcon = ({ type, size = 16, className = '' }) => {
    const iconProps = { size, className };
    switch (type) {
      case 'router': return <Router {...iconProps} />;
      case 'server': return <Server {...iconProps} />;
      case 'storage': return <HardDrive {...iconProps} />;
      case 'printer': return <Printer {...iconProps} />;
      case 'smartphone': return <Smartphone {...iconProps} />;
      case 'laptop': return <Laptop {...iconProps} />;
      case 'wifi': return <Wifi {...iconProps} />;
      default: return <Monitor {...iconProps} />;
    }
  };

  // Group hosts by subnet
  const groupedHosts = useMemo(() => {
    const groups = {};
    hosts.forEach(host => {
      const ip = host.ip_address || '';
      const subnet = ip.split('.').slice(0, 3).join('.') + '.0/24';
      if (!groups[subnet]) {
        groups[subnet] = [];
      }
      groups[subnet].push(host);
    });
    return groups;
  }, [hosts]);

  // Filter hosts
  const filteredHosts = useMemo(() => {
    let filtered = hosts;
    if (showVulnOnly) {
      filtered = filtered.filter(h => (h.vulnerability_count || 0) > 0);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(h =>
        h.ip_address?.toLowerCase().includes(q) ||
        h.hostname?.toLowerCase().includes(q) ||
        h.os_name?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [hosts, showVulnOnly, searchQuery]);

  // Statistics
  const stats = useMemo(() => ({
    total: hosts.length,
    vulnerable: hosts.filter(h => (h.vulnerability_count || 0) > 0).length,
    critical: hosts.filter(h => (h.vulnerability_count || 0) > 5).length,
    subnets: Object.keys(groupedHosts).length,
    services: hosts.reduce((sum, h) => sum + (Array.isArray(h.ports) ? h.ports.length : (h.open_ports || 0)), 0),
    online: hosts.filter(h => h.status === 'up').length
  }), [hosts, groupedHosts]);

  // AI Assistant Handler
  const handleAIQuery = useCallback(async (query) => {
    setAiLoading(true);
    setAiResponse(null);

    try {
      const response = await fetch('/api/v1/ai/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          context: {
            type: 'network_map',
            hosts: hosts.slice(0, 20).map(h => ({
              ip: h.ip_address,
              hostname: h.hostname,
              os: h.os_name,
              vulns: h.vulnerability_count,
              ports: (h.ports || []).slice(0, 5).map(p => p.port)
            })),
            stats
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response);
      } else {
        setAiResponse(generateLocalAIResponse(query));
      }
    } catch (error) {
      setAiResponse(generateLocalAIResponse(query));
    } finally {
      setAiLoading(false);
    }
  }, [hosts, stats]);

  // Local AI Fallback
  const generateLocalAIResponse = (query) => {
    const q = query.toLowerCase();

    if (q.includes('vulnérab') || q.includes('vuln') || q.includes('risque')) {
      const vulnHosts = hosts.filter(h => (h.vulnerability_count || 0) > 0);
      return `**Analyse des vulnérabilités réseau:**\n\n` +
        `- **${stats.vulnerable}** hôtes vulnérables sur ${stats.total}\n` +
        `- **${stats.critical}** hôtes critiques (>5 vulns)\n\n` +
        `**Hôtes les plus à risque:**\n` +
        vulnHosts.slice(0, 5).map(h => `- ${h.ip_address} (${h.hostname || 'N/D'}): ${h.vulnerability_count || 0} vulns`).join('\n') +
        `\n\n**Recommandation:** Priorisez les hôtes critiques pour la remédiation.`;
    }

    if (q.includes('topolog') || q.includes('structure') || q.includes('réseau')) {
      return `**Topologie du réseau:**\n\n` +
        `- **${stats.subnets}** sous-réseaux détectés\n` +
        `- **${stats.total}** hôtes au total\n` +
        `- **${stats.services}** services exposés\n\n` +
        `**Sous-réseaux:**\n` +
        Object.entries(groupedHosts).map(([subnet, hosts]) =>
          `- ${subnet}: ${hosts.length} hôtes`
        ).join('\n') +
        `\n\n**Conseil:** Cliquez sur un nœud pour voir ses détails et actions disponibles.`;
    }

    if (q.includes('scan') || q.includes('action') || q.includes('faire')) {
      return `**Actions disponibles sur les nœuds:**\n\n` +
        `1. **Clic gauche** - Sélectionner et voir les détails\n` +
        `2. **Clic droit** - Menu d'actions rapides:\n` +
        `   - 🔍 Scanner les ports\n` +
        `   - 🐛 Scan de vulnérabilités\n` +
        `   - 💻 Ouvrir terminal\n` +
        `   - 🎯 Simuler une attaque\n` +
        `   - 📋 Copier l'IP\n\n` +
        `**Conseil:** Activez le mode "Simulateur d'Attaque" pour planifier des attaques.`;
    }

    if (q.includes('attaq') || q.includes('exploit') || q.includes('pénétr')) {
      return `**Guide de simulation d'attaque:**\n\n` +
        `1. Activez le **Simulateur d'Attaque** en haut\n` +
        `2. Cliquez sur une cible sur la carte\n` +
        `3. L'IA analysera les vecteurs d'attaque\n` +
        `4. Lancez la simulation étape par étape\n\n` +
        `**Phases d'attaque:**\n` +
        `- 🔍 Reconnaissance\n` +
        `- 📋 Enumération\n` +
        `- 💥 Exploitation\n` +
        `- 🎯 Post-Exploitation\n\n` +
        `**Note:** Simulation uniquement, aucun impact réel.`;
    }

    return `**Aide Carte Réseau :**\n\n` +
      `Votre réseau contient **${stats.total}** hôtes répartis sur **${stats.subnets}** sous-réseaux.\n\n` +
      `**Pour commencer:**\n` +
      `- Cliquez sur un nœud pour le sélectionner\n` +
      `- Clic droit pour les actions rapides\n` +
      `- Utilisez les filtres pour affiner la vue\n` +
      `- Activez le Simulateur d'Attaque pour tester\n\n` +
      `**Posez-moi des questions sur:**\n` +
      `- Les vulnérabilités du réseau\n` +
      `- La topologie et structure\n` +
      `- Les actions possibles\n` +
      `- Les simulations d'attaque`;
  };

  // Quick Actions for nodes
  const nodeActions = [
    { id: 'scan_ports', icon: Scan, label: 'Scanner les ports', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { id: 'scan_vulns', icon: Bug, label: 'Scan vulnérabilités', color: 'text-red-400', bg: 'bg-red-500/20' },
    { id: 'terminal', icon: Terminal, label: 'Ouvrir terminal', color: 'text-green-400', bg: 'bg-green-500/20' },
    { id: 'attack', icon: Crosshair, label: 'Simuler attaque', color: 'text-orange-400', bg: 'bg-orange-500/20' },
    { id: 'copy_ip', icon: Copy, label: 'Copier IP', color: 'text-gray-400', bg: 'bg-gray-500/20' },
    { id: 'details', icon: FileText, label: 'Voir détails', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  ];

  const handleNodeAction = async (action, host) => {
    setNodeMenuOpen(null);
    setActionLoading(true);
    setActionResult(null);

    switch (action) {
      case 'copy_ip':
        navigator.clipboard.writeText(host.ip_address);
        setActionResult({ success: true, message: `IP ${host.ip_address} copiée !` });
        setActionLoading(false);
        setTimeout(() => setActionResult(null), 2000);
        break;

      case 'details':
        setSelectedHost(host);
        setActionLoading(false);
        break;

      case 'scan_ports':
        setQuickActionTarget(host);
        setShowQuickActions(true);
        try {
          const response = await fetch('/api/v1/scan/quick-ports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target: host.ip_address })
          });
          if (response.ok) {
            const data = await response.json();
            setActionResult({ success: true, message: 'Scan de ports lancé', data });
          } else {
            setActionResult({ success: true, message: `Scan de ports lancé sur ${host.ip_address}` });
          }
        } catch {
          setActionResult({ success: true, message: `Scan de ports en cours sur ${host.ip_address}...\nCommande: nmap -sV ${host.ip_address}` });
        }
        setActionLoading(false);
        break;

      case 'scan_vulns':
        setQuickActionTarget(host);
        setShowQuickActions(true);
        setActionResult({ success: true, message: `Scan de vulnérabilités lancé sur ${host.ip_address}...\nCommande: nmap --script vuln ${host.ip_address}` });
        setActionLoading(false);
        break;

      case 'terminal':
        setQuickActionTarget(host);
        setShowQuickActions(true);
        setActionResult({
          success: true,
          message: `Terminal SSH vers ${host.ip_address}\n\nCommande suggérée:\nssh root@${host.ip_address}\n\nOu utilisez:\nnc -v ${host.ip_address} [PORT]`
        });
        setActionLoading(false);
        break;

      case 'attack':
        setAttackSimulatorEnabled(true);
        handleTargetSelect(host);
        setActionLoading(false);
        break;

      default:
        setActionLoading(false);
    }
  };

  // Attack Strategy Generator
  const generateAttackStrategy = (target) => {
    const vulnCount = target.vulnerability_count || 0;
    const openPorts = target.ports || [];
    const os = target.os_name || 'Inconnu';

    let strategy = [];
    let recommendations = [];

    const hasSSH = openPorts.some(p => p.port === 22 || p.service === 'ssh');
    const hasRDP = openPorts.some(p => p.port === 3389 || p.service === 'rdp');
    const hasHTTP = openPorts.some(p => [80, 443, 8080, 8443].includes(p.port));
    const hasSMB = openPorts.some(p => [139, 445].includes(p.port));
    const hasSQL = openPorts.some(p => [1433, 3306, 5432].includes(p.port));

    strategy.push({
      phase: 'Reconnaissance',
      icon: '🔍',
      steps: [
        `nmap -sV -sC -p- ${target.ip_address}`,
        'Enumération des services',
        'Recherche de CVE connus'
      ],
      status: 'ready'
    });

    let enumSteps = ['Collecte d\'informations'];
    if (hasHTTP) enumSteps.push('Enumération web: gobuster, nikto');
    if (hasSMB) enumSteps.push('Enumération SMB: enum4linux');
    if (hasSQL) enumSteps.push('Enumération base de données');

    strategy.push({
      phase: 'Enumération',
      icon: '📋',
      steps: enumSteps,
      status: 'pending'
    });

    let exploitSteps = [];
    if (hasHTTP) exploitSteps.push('Test injection SQL/XSS');
    if (hasSSH) exploitSteps.push('Bruteforce SSH avec hydra');
    if (hasRDP) exploitSteps.push('Test BlueKeep (CVE-2019-0708)');
    if (hasSMB) exploitSteps.push('Test EternalBlue (MS17-010)');
    if (exploitSteps.length === 0) exploitSteps.push('Analyse des vecteurs d\'attaque');

    strategy.push({
      phase: 'Exploitation',
      icon: '💥',
      steps: exploitSteps,
      status: 'pending'
    });

    strategy.push({
      phase: 'Post-Exploitation',
      icon: '🎯',
      steps: ['Maintien d\'accès', 'Escalade de privilèges', 'Mouvement latéral'],
      status: 'pending'
    });

    if (vulnCount > 5) {
      recommendations.push({ type: 'critical', text: `Cible critique: ${vulnCount} vulnérabilités détectées` });
    }
    if (hasSMB) {
      recommendations.push({ type: 'warning', text: 'SMB exposé - Vérifier EternalBlue' });
    }
    if (hasHTTP) {
      recommendations.push({ type: 'info', text: 'Services web - Lancer analyse OWASP' });
    }

    return { strategy, recommendations };
  };

  const handleTargetSelect = (host) => {
    setSelectedTarget(host);
    const { strategy } = generateAttackStrategy(host);
    setAttackPath(strategy);
    setAttackStep(0);
    setAttackResults([]);
    setIsAttackRunning(false);
  };

  const runAttackSimulation = () => {
    setIsAttackRunning(true);
    setAttackStep(0);
    setAttackResults([]);
  };

  useEffect(() => {
    if (isAttackRunning && attackStep < attackPath.length) {
      const timer = setTimeout(() => {
        const currentPhase = attackPath[attackStep];
        const success = Math.random() > 0.3;

        setAttackResults(prev => [...prev, {
          phase: currentPhase.phase,
          success,
          message: success ? `${currentPhase.phase} réussie` : `${currentPhase.phase} en cours...`,
          timestamp: new Date().toLocaleTimeString()
        }]);

        setAttackPath(prev => prev.map((p, i) => ({
          ...p,
          status: i < attackStep ? 'completed' : i === attackStep ? (success ? 'success' : 'warning') : 'pending'
        })));

        setAttackStep(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (attackStep >= attackPath.length && isAttackRunning) {
      setIsAttackRunning(false);
    }
  }, [isAttackRunning, attackStep, attackPath.length]);

  // Enhanced Network Graph Rendering
  const renderNetworkGraph = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = containerRef.current?.clientWidth || 1200;
    const height = 750;

    // Define gradients and filters
    const defs = svg.append('defs');

    // Glow filter
    const filter = defs.append('filter')
      .attr('id', 'glow')
      .attr('x', '-50%').attr('y', '-50%')
      .attr('width', '200%').attr('height', '200%');
    filter.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Pulse animation filter
    const pulseFilter = defs.append('filter')
      .attr('id', 'pulse')
      .attr('x', '-100%').attr('y', '-100%')
      .attr('width', '300%').attr('height', '300%');
    pulseFilter.append('feGaussianBlur').attr('stdDeviation', '8').attr('result', 'coloredBlur');
    const pulseMerge = pulseFilter.append('feMerge');
    pulseMerge.append('feMergeNode').attr('in', 'coloredBlur');
    pulseMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Gradient for gateway
    const gatewayGradient = defs.append('radialGradient').attr('id', 'gatewayGradient');
    gatewayGradient.append('stop').attr('offset', '0%').attr('stop-color', '#1a3a1a');
    gatewayGradient.append('stop').attr('offset', '100%').attr('stop-color', '#0a0a0a');

    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });

    svg.call(zoom);
    const g = svg.append('g');

    // Grid background pattern
    const gridSize = 50;
    const gridPattern = defs.append('pattern')
      .attr('id', 'grid')
      .attr('width', gridSize)
      .attr('height', gridSize)
      .attr('patternUnits', 'userSpaceOnUse');
    gridPattern.append('path')
      .attr('d', `M ${gridSize} 0 L 0 0 0 ${gridSize}`)
      .attr('fill', 'none')
      .attr('stroke', '#99ce16')
      .attr('stroke-width', 0.3)
      .attr('stroke-opacity', 0.1);

    g.append('rect')
      .attr('width', width * 3)
      .attr('height', height * 3)
      .attr('x', -width)
      .attr('y', -height)
      .attr('fill', 'url(#grid)');

    // Prepare data
    const subnets = Object.keys(groupedHosts);
    const centerX = width / 2;
    const centerY = height / 2;

    // Gateway node - with subnet count for context
    const gatewayLabel = t('vpn.topology.networkGateway', 'Passerelle Réseau');
    const gatewayNode = { id: 'gateway', type: 'gateway', x: centerX, y: centerY, label: gatewayLabel, subnetCount: subnets.length };

    // Subnet nodes
    const subnetRadius = 250;
    const subnetNodes = subnets.map((subnet, idx) => {
      const angle = (2 * Math.PI * idx) / subnets.length - Math.PI / 2;
      return {
        id: subnet,
        type: 'subnet',
        x: centerX + subnetRadius * Math.cos(angle),
        y: centerY + subnetRadius * Math.sin(angle),
        label: subnet,
        hosts: groupedHosts[subnet]
      };
    });

    // Host nodes
    const hostNodes = [];
    const hostRadius = 140;
    subnetNodes.forEach(subnetNode => {
      const subnetHosts = subnetNode.hosts.filter(h =>
        (!showVulnOnly || (h.vulnerability_count || 0) > 0) &&
        (!searchQuery || h.ip_address?.includes(searchQuery) || h.hostname?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      subnetHosts.forEach((host, idx) => {
        const angle = (2 * Math.PI * idx) / Math.max(subnetHosts.length, 1) - Math.PI / 2;
        hostNodes.push({
          id: host.id,
          type: 'host',
          deviceType: getDeviceType(host),
          x: subnetNode.x + hostRadius * Math.cos(angle),
          y: subnetNode.y + hostRadius * Math.sin(angle),
          host: host,
          subnet: subnetNode.id
        });
      });
    });

    // Draw connections with animations
    const connections = g.append('g').attr('class', 'connections');

    // Gateway to subnet connections
    subnetNodes.forEach((subnet, idx) => {
      // Connection line
      connections.append('line')
        .attr('x1', gatewayNode.x)
        .attr('y1', gatewayNode.y)
        .attr('x2', subnet.x)
        .attr('y2', subnet.y)
        .attr('stroke', '#99ce16')
        .attr('stroke-width', 3)
        .attr('stroke-opacity', 0.3)
        .attr('stroke-dasharray', '10,5');

      // Animated particles
      if (animationsEnabled) {
        const particle = connections.append('circle')
          .attr('r', 5)
          .attr('fill', '#99ce16')
          .attr('filter', 'url(#glow)')
          .attr('opacity', 0.8);

        const animateParticle = () => {
          particle
            .attr('cx', gatewayNode.x)
            .attr('cy', gatewayNode.y)
            .transition()
            .duration(3000 + Math.random() * 1000)
            .ease(d3.easeLinear)
            .attr('cx', subnet.x)
            .attr('cy', subnet.y)
            .on('end', animateParticle);
        };
        setTimeout(animateParticle, idx * 500);
      }
    });

    // Subnet to host connections
    hostNodes.forEach(hostNode => {
      const subnetNode = subnetNodes.find(s => s.id === hostNode.subnet);
      if (subnetNode) {
        const isTarget = selectedTarget && hostNode.host.id === selectedTarget.id;
        const isSelected = selectedHost && hostNode.host.id === selectedHost.id;

        connections.append('line')
          .attr('x1', subnetNode.x)
          .attr('y1', subnetNode.y)
          .attr('x2', hostNode.x)
          .attr('y2', hostNode.y)
          .attr('stroke', isTarget ? '#ef4444' : isSelected ? '#99ce16' : '#99ce16')
          .attr('stroke-width', isTarget ? 3 : isSelected ? 2 : 1.5)
          .attr('stroke-opacity', isTarget ? 0.8 : isSelected ? 0.6 : 0.2)
          .attr('filter', isTarget ? 'url(#pulse)' : null);
      }
    });

    // Draw gateway (centered)
    const gatewayGroup = g.append('g')
      .attr('class', 'gateway')
      .attr('transform', `translate(${gatewayNode.x}, ${gatewayNode.y})`);

    // Pulsing outer ring
    if (animationsEnabled) {
      const pulseRing = gatewayGroup.append('circle')
        .attr('r', 55)
        .attr('fill', 'none')
        .attr('stroke', '#99ce16')
        .attr('stroke-width', 2)
        .attr('opacity', 0);

      const animatePulse = () => {
        pulseRing
          .attr('r', 55)
          .attr('opacity', 0.6)
          .transition()
          .duration(2000)
          .ease(d3.easeQuadOut)
          .attr('r', 100)
          .attr('opacity', 0)
          .on('end', animatePulse);
      };
      animatePulse();
    }

    gatewayGroup.append('circle')
      .attr('r', 55)
      .attr('fill', 'url(#gatewayGradient)')
      .attr('stroke', '#99ce16')
      .attr('stroke-width', 4)
      .attr('filter', 'url(#glow)');

    // Gateway icon (globe)
    gatewayGroup.append('text')
      .text('🌐')
      .attr('text-anchor', 'middle')
      .attr('dy', 8)
      .attr('font-size', '28px');

    if (showLabels) {
      // Gateway main label
      gatewayGroup.append('text')
        .text(gatewayNode.label)
        .attr('text-anchor', 'middle')
        .attr('dy', 80)
        .attr('fill', '#99ce16')
        .attr('font-size', '14px')
        .attr('font-weight', '600');

      // Subnet count for context
      const subnetsCountText = t('vpn.topology.subnetsCount', '{{count}} sous-réseaux', { count: gatewayNode.subnetCount });
      gatewayGroup.append('text')
        .text(subnetsCountText)
        .attr('text-anchor', 'middle')
        .attr('dy', 98)
        .attr('fill', '#6b7280')
        .attr('font-size', '11px')
        .attr('font-weight', '400');
    }

    // Draw subnet nodes
    subnetNodes.forEach(subnet => {
      const subnetGroup = g.append('g')
        .attr('class', 'subnet')
        .attr('transform', `translate(${subnet.x}, ${subnet.y})`);

      // Hexagon shape for subnets
      const hexPoints = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        hexPoints.push([45 * Math.cos(angle), 45 * Math.sin(angle)]);
      }

      subnetGroup.append('polygon')
        .attr('points', hexPoints.map(p => p.join(',')).join(' '))
        .attr('fill', '#0a150a')
        .attr('stroke', '#99ce16')
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.7);

      subnetGroup.append('text')
        .text(subnet.hosts.length)
        .attr('text-anchor', 'middle')
        .attr('dy', 8)
        .attr('fill', '#99ce16')
        .attr('font-size', '22px')
        .attr('font-weight', 'bold');

      if (showLabels) {
        subnetGroup.append('text')
          .text(subnet.label.replace('/24', '.*'))
          .attr('text-anchor', 'middle')
          .attr('dy', 70)
          .attr('fill', '#666')
          .attr('font-size', '11px')
          .attr('font-family', 'monospace');
      }
    });

    // Draw host nodes with enhanced design
    hostNodes.forEach(hostNode => {
      const host = hostNode.host;
      const vulnCount = host.vulnerability_count || 0;
      const hasVulns = vulnCount > 0;
      const isCritical = vulnCount > 5;
      const isTarget = selectedTarget && host.id === selectedTarget.id;
      const isSelected = selectedHost && host.id === selectedHost.id;

      const hostGroup = g.append('g')
        .attr('class', 'host')
        .attr('transform', `translate(${hostNode.x}, ${hostNode.y})`)
        .style('cursor', 'pointer');

      // Hover area (larger for easier clicking)
      hostGroup.append('circle')
        .attr('r', 40)
        .attr('fill', 'transparent')
        .on('click', () => {
          setSelectedHost(host);
          if (attackSimulatorEnabled) {
            handleTargetSelect(host);
          }
        })
        .on('contextmenu', (event) => {
          event.preventDefault();
          setNodeMenuOpen(host.id);
          setNodeMenuPosition({ x: hostNode.x, y: hostNode.y });
        });

      // Gradient for node
      const nodeGradient = defs.append('radialGradient').attr('id', `nodeGradient-${host.id}`);
      nodeGradient.append('stop').attr('offset', '0%').attr('stop-color', isTarget ? '#2a1010' : '#101810');
      nodeGradient.append('stop').attr('offset', '100%').attr('stop-color', '#050505');

      // Outer glow for selected/target
      if (isSelected || isTarget) {
        hostGroup.append('circle')
          .attr('r', 40)
          .attr('fill', 'none')
          .attr('stroke', isTarget ? '#ef4444' : '#99ce16')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', isTarget ? '0' : '5,3')
          .attr('opacity', 0.6)
          .attr('filter', 'url(#glow)');
      }

      // Main node circle
      hostGroup.append('circle')
        .attr('r', 32)
        .attr('fill', `url(#nodeGradient-${host.id})`)
        .attr('stroke', isTarget ? '#ef4444' : isCritical ? '#ef4444' : hasVulns ? '#f59e0b' : '#10b981')
        .attr('stroke-width', isSelected ? 3 : 2)
        .attr('filter', isTarget || isSelected ? 'url(#glow)' : null)
        .on('mouseover', function() {
          d3.select(this).attr('stroke-width', 4);
        })
        .on('mouseout', function() {
          d3.select(this).attr('stroke-width', isSelected ? 3 : 2);
        });

      // Device icon
      const iconMap = {
        router: '🌐', server: '🖥️', storage: '💾', printer: '🖨️',
        smartphone: '📱', laptop: '💻', wifi: '📶', workstation: '🖥️'
      };
      hostGroup.append('text')
        .text(iconMap[hostNode.deviceType] || '🖥️')
        .attr('text-anchor', 'middle')
        .attr('dy', 8)
        .attr('font-size', '20px');

      // Vulnerability badge with animation
      if (hasVulns) {
        const badgeGroup = hostGroup.append('g')
          .attr('transform', 'translate(22, -22)');

        badgeGroup.append('circle')
          .attr('r', 14)
          .attr('fill', isCritical ? '#ef4444' : '#f59e0b')
          .attr('stroke', '#0a0a0a')
          .attr('stroke-width', 2);

        if (isCritical && animationsEnabled) {
          badgeGroup.append('circle')
            .attr('r', 14)
            .attr('fill', 'none')
            .attr('stroke', '#ef4444')
            .attr('stroke-width', 2)
            .attr('opacity', 0)
            .each(function() {
              const pulse = d3.select(this);
              const animateBadgePulse = () => {
                pulse
                  .attr('r', 14)
                  .attr('opacity', 0.8)
                  .transition()
                  .duration(1500)
                  .attr('r', 25)
                  .attr('opacity', 0)
                  .on('end', animateBadgePulse);
              };
              animateBadgePulse();
            });
        }

        badgeGroup.append('text')
          .text(vulnCount > 99 ? '99+' : vulnCount)
          .attr('text-anchor', 'middle')
          .attr('dy', 5)
          .attr('fill', 'white')
          .attr('font-size', vulnCount > 9 ? '10px' : '12px')
          .attr('font-weight', 'bold');
      }

      // Status indicator (online/offline)
      hostGroup.append('circle')
        .attr('cx', -22)
        .attr('cy', 22)
        .attr('r', 6)
        .attr('fill', host.status === 'up' ? '#10b981' : '#6b7280')
        .attr('stroke', '#0a0a0a')
        .attr('stroke-width', 2);

      // Label
      if (showLabels) {
        hostGroup.append('text')
          .text(host.hostname || host.ip_address)
          .attr('text-anchor', 'middle')
          .attr('dy', 55)
          .attr('fill', isTarget ? '#ef4444' : isSelected ? '#99ce16' : '#888')
          .attr('font-size', '11px')
          .attr('font-weight', isSelected ? '600' : '400')
          .each(function() {
            const text = d3.select(this);
            const currentText = text.text();
            if (currentText.length > 16) {
              text.text(currentText.substring(0, 14) + '...');
            }
          });
      }
    });

    // Center view
    const initialScale = 0.9;
    svg.call(zoom.transform, d3.zoomIdentity.translate(width * 0.05, height * 0.05).scale(initialScale));
  };

  const handleZoomIn = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(d3.zoom().scaleBy, 1.3);
  };

  const handleZoomOut = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(d3.zoom().scaleBy, 0.7);
  };

  const handleReset = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(d3.zoom().transform, d3.zoomIdentity);
  };

  const handleExportPNG = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = svgElement.clientWidth * 2;
    canvas.height = svgElement.clientHeight * 2;
    ctx.scale(2, 2);
    ctx.fillStyle = '#060606';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `network-map-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading && !selectedScan) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (scans.length === 0) {
    return (
      <div className="min-h-screen bg-brand-black p-8">
        <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto">
          <div className="text-center py-20 bg-gradient-to-b from-brand-black-light to-brand-black rounded-3xl border border-brand-green/20">
            <Network className="w-24 h-24 text-gray-700 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Aucune donnée réseau</h2>
            <p className="text-gray-400 mb-8 text-lg">Lancez un scan pour visualiser votre topologie réseau</p>
            <button onClick={() => navigate('/scans/new')} className="px-8 py-3 bg-brand-green text-black rounded-xl font-medium hover:bg-brand-green/90 transition-all">
              <Target className="w-5 h-5 mr-2 inline" />
              Nouveau scan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gradient-green flex items-center gap-3">
                <Network className="w-10 h-10 text-brand-green" />
                Carte Réseau
              </h1>
              <p className="text-gray-400 mt-2">Visualisation interactive de la topologie réseau</p>

              {/* Workspace Selector */}
              <div className="mt-3 relative">
                <button
                  onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-black-light border border-brand-green/30 rounded-lg hover:border-brand-green/60 transition-all"
                >
                  <Briefcase className="w-4 h-4 text-brand-green" />
                  <span className="text-white">
                    {selectedWorkspace
                      ? workspaces.find(w => w.id === selectedWorkspace)?.client_name || 'Espace de travail'
                      : 'Tous les scans'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${workspaceDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {workspaceDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-brand-black-light border border-brand-green/30 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => handleWorkspaceChange(null)}
                      className={`w-full px-4 py-2 text-left hover:bg-brand-green/10 flex items-center gap-2 ${!selectedWorkspace ? 'bg-brand-green/20 text-brand-green' : 'text-white'}`}
                    >
                      <Globe className="w-4 h-4" />
                      Tous les scans
                    </button>
                    <div className="border-t border-gray-700 my-1"></div>
                    {workspaces.map(workspace => (
                      <button
                        key={workspace.id}
                        onClick={() => handleWorkspaceChange(workspace.id)}
                        className={`w-full px-4 py-2 text-left hover:bg-brand-green/10 flex items-center gap-2 ${selectedWorkspace === workspace.id ? 'bg-brand-green/20 text-brand-green' : 'text-white'}`}
                      >
                        <Building2 className="w-4 h-4" />
                        <div className="flex-1 truncate">
                          <div className="font-medium">{workspace.client_name}</div>
                          {workspace.mission_type && (
                            <div className="text-xs text-gray-500">{workspace.mission_type}</div>
                          )}
                        </div>
                      </button>
                    ))}
                    {workspaces.length === 0 && (
                      <div className="px-4 py-3 text-gray-500 text-sm text-center">
                        Aucun workspace actif
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAIAssistant(!showAIAssistant)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                  showAIAssistant
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/50 hover:bg-purple-500/30'
                }`}
              >
                <Brain className="w-5 h-5" />
                Assistant IA
              </button>

              <button
                onClick={() => {
                  setAttackSimulatorEnabled(!attackSimulatorEnabled);
                  if (!attackSimulatorEnabled) {
                    setSelectedTarget(null);
                    setAttackPath([]);
                  }
                }}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                  attackSimulatorEnabled
                    ? 'bg-red-500 text-white'
                    : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                }`}
              >
                <Crosshair className="w-5 h-5" />
                Simulateur d'Attaque
              </button>

              <button
                onClick={() => fetchHostsForScan(selectedScan)}
                className="p-2.5 bg-brand-black-lighter border border-brand-green/50 rounded-lg hover:bg-brand-green/10 transition-all"
              >
                <RefreshCcw className="w-5 h-5 text-brand-green" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30 relative group cursor-help">
            <div className="text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-gray-400 text-sm truncate" title="Hôtes">Hôtes</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">Total des hôtes</span>
              </div>
              <p className="text-xs text-gray-300">Nombre total de machines et équipements découverts sur le réseau analysé. Inclut serveurs, postes de travail, équipements réseau et IoT.</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-green-500/30 relative group cursor-help">
            <div className="text-3xl font-bold text-green-400">{stats.online}</div>
            <div className="text-gray-400 text-sm truncate" title="En ligne">En ligne</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-green-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Wifi className="w-4 h-4 text-green-400" />
                <span className="font-semibold text-white">Hôtes en ligne</span>
              </div>
              <p className="text-xs text-gray-300">Machines actuellement actives et accessibles sur le réseau. Répondent aux requêtes ICMP/TCP et peuvent être ciblées pour des tests de sécurité.</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-yellow-500/30 relative group cursor-help">
            <div className="text-3xl font-bold text-yellow-400">{stats.vulnerable}</div>
            <div className="text-gray-400 text-sm truncate" title="Vulnérables">Vulnérables</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-yellow-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="font-semibold text-white">Hôtes vulnérables</span>
              </div>
              <p className="text-xs text-gray-300">Machines présentant des vulnérabilités de sécurité détectées (CVE, misconfigurations, services obsolètes). Nécessitent une attention pour renforcer la sécurité.</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/30 relative group cursor-help">
            <div className="text-3xl font-bold text-red-500">{stats.critical}</div>
            <div className="text-gray-400 text-sm truncate" title="Critiques">Critiques</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 text-red-500" />
                <span className="font-semibold text-white">Hôtes critiques</span>
              </div>
              <p className="text-xs text-gray-300">Machines avec vulnérabilités de niveau critique (CVSS 9+). Risque d'exploitation immédiate, compromission totale possible. Action prioritaire requise.</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-blue-500/30 relative group cursor-help">
            <div className="text-3xl font-bold text-blue-400">{stats.subnets}</div>
            <div className="text-gray-400 text-sm truncate" title="Sous-Réseaux">Sous-Réseaux</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Network className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">Sous-Réseaux</span>
              </div>
              <p className="text-xs text-gray-300">Segments réseau distincts découverts (VLANs, subnets IP). Permet d'identifier la segmentation réseau et les zones de sécurité potentielles.</p>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30 relative group cursor-help">
            <div className="text-3xl font-bold text-purple-400">{stats.services}</div>
            <div className="text-gray-400 text-sm truncate" title="Services">Services</div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-white">Services découverts</span>
              </div>
              <p className="text-xs text-gray-300">Nombre total de services réseau détectés (HTTP, SSH, SMB, RDP, etc.). Chaque service est un point d'entrée potentiel pour les tests de pénétration.</p>
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAIAssistant && (
          <div className="bg-brand-black-light rounded-xl p-6 border border-purple-500/30 mb-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-400" />
              Assistant IA - Carte Réseau
            </h3>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <button
                onClick={() => handleAIQuery('Quelles sont les vulnérabilités du réseau ?')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">🔍 Vulnérabilités</div>
                <div className="text-gray-500 text-xs">Analyser les risques</div>
              </button>
              <button
                onClick={() => handleAIQuery('Quelle est la structure du réseau ?')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">🗺️ Topologie</div>
                <div className="text-gray-500 text-xs">Structure réseau</div>
              </button>
              <button
                onClick={() => handleAIQuery('Quelles actions puis-je faire sur les nœuds ?')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">⚡ Actions</div>
                <div className="text-gray-500 text-xs">Guide des actions</div>
              </button>
              <button
                onClick={() => handleAIQuery('Comment simuler une attaque ?')}
                className="p-3 bg-brand-black rounded-lg border border-purple-500/30 hover:border-purple-500 transition-all text-left"
              >
                <div className="text-purple-400 font-medium">🎯 Attaques</div>
                <div className="text-gray-500 text-xs">Simulation pentest</div>
              </button>
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && aiQuery && handleAIQuery(aiQuery)}
                placeholder="Posez une question sur le réseau..."
                className="flex-1 px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
              />
              <button
                onClick={() => aiQuery && handleAIQuery(aiQuery)}
                disabled={aiLoading || !aiQuery}
                className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all disabled:opacity-50"
              >
                {aiLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : 'Analyser'}
              </button>
            </div>
            {aiResponse && (
              <div className="mt-4 p-4 bg-brand-black rounded-lg border border-purple-500/30">
                <MarkdownRenderer content={aiResponse} variant="compact" />
              </div>
            )}
          </div>
        )}

        {/* Controls Bar */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="min-w-[220px]">
                <select
                  value={selectedScan || ''}
                  onChange={(e) => setSelectedScan(e.target.value)}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
                >
                  {scans.map(scan => (
                    <option key={scan.id} value={scan.id}>
                      {scan.target} - {new Date(scan.created_at).toLocaleDateString('fr-FR')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par adresse ou nom..."
                  className="pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white w-48 focus:border-brand-green focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-4 border-l border-gray-700 pl-4">
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showLabels}
                    onChange={(e) => setShowLabels(e.target.checked)}
                    className="w-4 h-4 accent-brand-green rounded"
                  />
                  Étiquettes
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showVulnOnly}
                    onChange={(e) => setShowVulnOnly(e.target.checked)}
                    className="w-4 h-4 accent-brand-green rounded"
                  />
                  Vulnérables seulement
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={animationsEnabled}
                    onChange={(e) => setAnimationsEnabled(e.target.checked)}
                    className="w-4 h-4 accent-brand-green rounded"
                  />
                  Animations
                </label>
              </div>
            </div>

            <div className="text-sm text-gray-500">
              Clic droit sur un nœud pour les actions
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Network Map */}
          <div ref={containerRef} className="xl:col-span-3 bg-gradient-to-b from-brand-black-light to-brand-black rounded-2xl border border-brand-green/30 relative overflow-hidden">
            {/* Map Controls */}
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button onClick={handleZoomIn} className="p-2.5 bg-brand-black/90 border border-brand-green/50 rounded-lg hover:bg-brand-green/20 transition-all backdrop-blur" title="Agrandir">
                <ZoomIn className="w-4 h-4 text-brand-green" />
              </button>
              <button onClick={handleZoomOut} className="p-2.5 bg-brand-black/90 border border-brand-green/50 rounded-lg hover:bg-brand-green/20 transition-all backdrop-blur" title="Réduire">
                <ZoomOut className="w-4 h-4 text-brand-green" />
              </button>
              <button onClick={handleReset} className="p-2.5 bg-brand-black/90 border border-brand-green/50 rounded-lg hover:bg-brand-green/20 transition-all backdrop-blur" title="Réinitialiser">
                <Maximize className="w-4 h-4 text-brand-green" />
              </button>
              <button onClick={handleExportPNG} className="p-2.5 bg-brand-black/90 border border-brand-green/50 rounded-lg hover:bg-brand-green/20 transition-all backdrop-blur" title="Exporter PNG">
                <Download className="w-4 h-4 text-brand-green" />
              </button>
            </div>

            {/* Zoom Level */}
            <div className="absolute top-4 left-4 z-10 bg-brand-black/90 border border-brand-green/50 rounded-lg px-3 py-1.5 backdrop-blur">
              <span className="text-xs text-gray-400">Zoom: <span className="text-brand-green font-bold">{Math.round((zoomLevel || 1) * 100)}%</span></span>
            </div>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-10 bg-brand-black/90 border border-brand-green/50 rounded-lg p-4 backdrop-blur min-w-[140px]">
              <div className="text-sm font-bold text-brand-green mb-3 whitespace-nowrap">LÉGENDE</div>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full border-2 border-green-500 flex-shrink-0"></div>
                  <span className="text-gray-300 whitespace-nowrap">Sécurisé</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full border-2 border-yellow-500 flex-shrink-0"></div>
                  <span className="text-gray-300 whitespace-nowrap">Vulnérable</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full border-2 border-red-500 flex-shrink-0"></div>
                  <span className="text-gray-300 whitespace-nowrap">Critique</span>
                </div>
              </div>
            </div>

            {/* Attack Mode Indicator */}
            {attackSimulatorEnabled && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 backdrop-blur">
                <div className="flex items-center gap-2 text-red-400">
                  <Crosshair className="w-4 h-4 animate-pulse" />
                  <span className="text-sm font-medium">Mode Attaque - Cliquez sur une cible</span>
                </div>
              </div>
            )}

            {loading ? (
              <div className="h-[750px] flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Chargement...</p>
                </div>
              </div>
            ) : (
              <svg
                ref={svgRef}
                className="w-full h-[750px]"
                style={{ background: 'radial-gradient(ellipse at center, #080808 0%, #030303 100%)' }}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Attack Simulator Panel */}
            {attackSimulatorEnabled && selectedTarget && (
              <div className="bg-gradient-to-b from-red-500/10 to-brand-black-light rounded-xl border border-red-500/30 overflow-hidden">
                <div className="p-4 border-b border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-red-400" />
                    <div>
                      <h3 className="font-bold text-red-400">Cible sélectionnée</h3>
                      <p className="text-xs text-gray-400">{selectedTarget.hostname || selectedTarget.ip_address}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-brand-black rounded-lg p-2 border border-gray-800">
                      <div className="text-xs text-gray-500">IP</div>
                      <div className="text-white font-mono text-sm">{selectedTarget.ip_address}</div>
                    </div>
                    <div className="bg-brand-black rounded-lg p-2 border border-gray-800">
                      <div className="text-xs text-gray-500">Vulnér.</div>
                      <div className="text-red-400 font-bold">{selectedTarget.vulnerability_count || 0}</div>
                    </div>
                  </div>

                  <button
                    onClick={runAttackSimulation}
                    disabled={isAttackRunning}
                    className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                      isAttackRunning ? 'bg-gray-700 text-gray-400' : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >
                    {isAttackRunning ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Simulation...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Lancer Simulation
                      </>
                    )}
                  </button>

                  {/* Attack Phases */}
                  {attackPath.length > 0 && (
                    <div className="space-y-2">
                      {attackPath.map((phase, idx) => (
                        <div
                          key={idx}
                          className={`p-2 rounded-lg border text-xs ${
                            phase.status === 'success' ? 'bg-green-500/10 border-green-500/50 text-green-400' :
                            phase.status === 'warning' ? 'bg-yellow-500/10 border-yellow-500/50 text-yellow-400' :
                            attackStep === idx ? 'bg-brand-green/10 border-brand-green/50 text-brand-green' :
                            'bg-brand-black border-gray-800 text-gray-500'
                          }`}
                        >
                          <span className="mr-2">{phase.icon}</span>
                          {phase.phase}
                          {phase.status === 'success' && <CheckCircle className="w-3 h-3 inline ml-2" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Selected Host Details */}
            {selectedHost && (
              <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
                <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DeviceIcon type={getDeviceType(selectedHost)} size={20} className="text-brand-green" />
                    <h3 className="font-bold text-white">Détails</h3>
                  </div>
                  <button onClick={() => setSelectedHost(null)} className="text-gray-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                  <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                    <span className="text-xs text-gray-500">Adresse IP</span>
                    <div className="text-white font-mono">{selectedHost.ip_address}</div>
                  </div>

                  {selectedHost.hostname && (
                    <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <span className="text-xs text-gray-500">Nom d'hôte</span>
                      <div className="text-white">{selectedHost.hostname}</div>
                    </div>
                  )}

                  {selectedHost.os_name && (() => {
                    const osInfo = getOSIcon(selectedHost.os_name);
                    const OSIcon = osInfo.icon;
                    return (
                      <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                        <span className="text-xs text-gray-500">OS</span>
                        <div className="flex items-center gap-2 text-white text-sm">
                          <OSIcon className={`w-4 h-4 ${osInfo.color}`} />
                          <span>{selectedHost.os_name}</span>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <span className="text-xs text-gray-500">Statut</span>
                      <div className={`font-bold ${selectedHost.status === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                        {selectedHost.status === 'up' ? 'En ligne' : 'Hors ligne'}
                      </div>
                    </div>
                    <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <span className="text-xs text-gray-500">Vulnér.</span>
                      <div className={`font-bold ${(selectedHost.vulnerability_count || 0) > 5 ? 'text-red-400' : (selectedHost.vulnerability_count || 0) > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {selectedHost.vulnerability_count || 0}
                      </div>
                    </div>
                  </div>

                  {/* Open Ports */}
                  {selectedHost.ports && selectedHost.ports.length > 0 && (
                    <div>
                      <div className="text-xs text-gray-500 mb-2">Ports ouverts ({selectedHost.ports.length})</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedHost.ports.slice(0, 8).map((port, idx) => (
                          <span key={idx} className="px-2 py-1 bg-brand-black rounded text-xs text-brand-green font-mono border border-gray-800">
                            {port.port ?? 'N/A'}/{port.protocol || 'tcp'}
                          </span>
                        ))}
                        {selectedHost.ports.length > 8 && (
                          <span className="px-2 py-1 text-xs text-gray-500">+{selectedHost.ports.length - 8}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="pt-3 space-y-2 border-t border-gray-800">
                    <div className="text-xs text-gray-500 mb-2">Actions rapides</div>
                    <div className="grid grid-cols-2 gap-2">
                      {nodeActions.slice(0, 4).map(action => (
                        <button
                          key={action.id}
                          onClick={() => handleNodeAction(action.id, selectedHost)}
                          className={`p-2 ${action.bg} ${action.color} rounded-lg text-xs flex items-center gap-2 hover:opacity-80 transition-all`}
                        >
                          <action.icon className="w-3.5 h-3.5" />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Host List */}
            <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-brand-green" />
                  Hôtes ({filteredHosts.length})
                </h3>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {filteredHosts.slice(0, 20).map(host => (
                  <div
                    key={host.id}
                    onClick={() => setSelectedHost(host)}
                    className={`p-3 border-b border-gray-800 cursor-pointer transition-all hover:bg-brand-black ${
                      selectedHost?.id === host.id ? 'bg-brand-green/10 border-l-2 border-l-brand-green' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DeviceIcon type={getDeviceType(host)} size={14} className="text-gray-500" />
                        <span className="text-white text-sm font-mono">{host.ip_address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {(host.vulnerability_count || 0) > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            (host.vulnerability_count || 0) > 5 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {host.vulnerability_count || 0}
                          </span>
                        )}
                        <div className={`w-2 h-2 rounded-full ${host.status === 'up' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                      </div>
                    </div>
                    {host.hostname && (
                      <div className="text-xs text-gray-500 mt-1 truncate">{host.hostname}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Action Modal */}
        {showQuickActions && quickActionTarget && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowQuickActions(false)}>
            <div className="bg-brand-black-light rounded-2xl border border-brand-green/30 p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Action sur {quickActionTarget.ip_address}</h3>
                <button onClick={() => setShowQuickActions(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {actionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : actionResult && (
                <div className={`p-4 rounded-lg ${actionResult.success ? 'bg-brand-green/10 border border-brand-green/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{actionResult.message}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Result Toast */}
        {actionResult && !showQuickActions && (
          <div className="fixed bottom-6 right-6 z-50 bg-brand-black-light border border-brand-green/30 rounded-lg p-4 shadow-2xl animate-fade-in">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-brand-green" />
              <span className="text-white">{actionResult.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkMap;
